import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Users, CircleDot, Circle } from "lucide-react";

/**
 * Direct-reports rollup for any supervisor. Reuses the same rollup shape as
 * /org/units/{id}/rollup so the render logic can be lifted later if we
 * introduce a "My unit" view.
 */
export default function EmployeeDirectReports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/direct-reports").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="direct-reports-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUPERVISOR VIEW</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Direct <em className="italic text-kb-primary">reports</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Everyone assigned to you as their line manager, <strong>plus every member of any Org Unit you head</strong>. Click a card to open their full task board — attendance for today shown at a glance.</p>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        rows.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white">
            <Users className="w-8 h-8 text-kb-ink-light mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-kb-ink-muted">No direct reports yet. An admin can either assign you as someone&apos;s supervisor from <strong>Admin → Users</strong>, or make you the Head of an Org Unit at <strong>Admin → Org Units</strong> — every member of that unit will show up here automatically.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            {rows.map((u) => (
              <Link key={u.user_id} to={`/dashboard/employee/direct-reports/${u.user_id}`} className="kb-card p-4 flex flex-wrap items-center gap-3 hover:border-kb-primary transition-colors" data-testid={`direct-report-${u.user_id}`}>
                {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> :
                  <div className="w-9 h-9 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-xs text-kb-primary">{u.name?.[0]?.toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-kb-ink truncate flex items-center gap-2">
                    {u.name}
                    {u.via_unit && <span className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-indigo-50 border border-indigo-200 text-indigo-700" title="Reports to you via an Org Unit you head">via unit</span>}
                  </div>
                  <div className="kb-mono text-[10px] text-kb-ink-muted truncate">
                    {u.role}{u.employee_role ? ` · ${u.employee_role}` : ""}{u.unit_role ? ` · ${u.unit_role}` : (u.team_section ? ` · ${u.team_section}` : "")}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 kb-mono text-[10px] uppercase tracking-wider">
                  <span className="px-1.5 py-0.5 rounded-sm border border-slate-200 text-kb-ink-muted">{u.tasks.open} open</span>
                  {u.tasks.overdue > 0 && <span className="px-1.5 py-0.5 rounded-sm border border-rose-200 bg-rose-50 text-rose-700">{u.tasks.overdue} overdue</span>}
                  <span className="px-1.5 py-0.5 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700">{u.tasks.done} done</span>
                  {u.attendance_today.clocked_in ? (
                    <span title={`Clocked in · ${new Date(u.attendance_today.last_at).toLocaleTimeString()}`} className="inline-flex items-center gap-1 text-emerald-700"><CircleDot className="w-3 h-3" /> in</span>
                  ) : u.attendance_today.last_at ? (
                    <span title={`Last activity ${new Date(u.attendance_today.last_at).toLocaleTimeString()}`} className="inline-flex items-center gap-1 text-kb-ink-muted"><Circle className="w-3 h-3" /> out</span>
                  ) : (
                    <span className="text-kb-ink-light">—</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}
